import _ from 'underscore';
import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { Theme, ThemeImage, ThemesRecommendationDialog } from '@bingads-webui/inline-copilot-ui';

import { useCopilotRepositoryContext } from '../CopilotRepositoryProvider';
import {
  AssetsRecommendationData,
  RecommendationError,
  TextAssetRecommendation,
  ThemeWithAssets,
} from '../../types';

export const getThemeRecommendationErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case RecommendationError.TOO_MANY_REQUESTS:
      return _TL_('You\'ve reached the daily limit of requests for asset recommendations. Please try again later.');
    case RecommendationError.NO_RECOMMENDATION:
    default:
      return _TL_('We\'re not able to generate a recommendation from the provided text. Please enter a new description and try again.');
  }
};

export interface ICopilotThemesRecommendationDialogProps {
  onApply: (selectedThemes: Theme[], selectedThemesAndAssets: ThemeWithAssets[]) => Promise<void>;
  onApplyFailed?: (error: Error) => void;
  onApplySucceeded?: () => void;
  onClosed?: () => void;
  onFetch?: () => void,
  onFetchAssetsSucceeded?: (finalUrl: string, assetsResponse: AssetsRecommendationData) => void;
  onFetchFailed?: (error: Error) => void;
  onFetchSucceeded?: (finalUrl: string, themes: Theme[]) => void;
  onSelectedThemesChanged?: (themes: Theme[]) => void;
  copyOverrides?: {
    title?: string,
    finalUrl?: string,
    description?: string,
    helpText?: string,
    helpTopic?: string,
    helpTopicAlt?: string,
    chooseThemes?: string,
    inputPlaceholder?: string,
    loadingText?: string,
    applyingText?: string,
  };
  open: boolean;
  isNewLogo?: boolean;
}

export const CopilotThemesRecommendationDialog: React.FC<ICopilotThemesRecommendationDialogProps> = (props) => {
  const {
    onApply,
    onApplyFailed,
    onApplySucceeded,
    onClosed,
    onFetch,
    onFetchAssetsSucceeded,
    onFetchFailed,
    onFetchSucceeded,
    onSelectedThemesChanged,
    open,
    isNewLogo,
    copyOverrides,
  } = props;

  const i18n = useContext(I18nContext) as I18nModel;
  const repository = useCopilotRepositoryContext();

  const finalUrl = repository?.finalUrl || '';

  const [fetchThemeAssetsPromise, setFetchThemeAssetsPromise] = useState<Promise<AssetsRecommendationData> | undefined>();

  const fetch = useCallback((prompt: string): Promise<Theme[]> => {
    const fetchPerfMarker = repository?.perfMarker?.createChild('fetchThemes');
    fetchPerfMarker?.willInit();

    // fetch the list of themes based on the prompt text
    onFetch?.();
    fetchPerfMarker?.willFetchData();
    return repository!.fetchThemes(prompt)
      .then((themesResponse) => {
        const themes = themesResponse?.Themes;
        if (themes?.length > 0) {
          // start fetching the assets for these themes
          const fetchAssetsPerfMarker = repository?.perfMarker?.createChild('fetchThemeAssets');
          fetchAssetsPerfMarker?.willInit();
          const themeNames = themes.map((theme) => theme.Name);

          fetchAssetsPerfMarker?.willFetchData();
          setFetchThemeAssetsPromise(repository!.fetchAssets(prompt, themeNames).then((assetsResponse) => {
            fetchAssetsPerfMarker?.willRender();
            return assetsResponse;
          }).finally(() => {
            fetchAssetsPerfMarker?.done();
          }));
        }

        fetchPerfMarker?.willRender();
        return themes;
      })
      .catch((error: { Code: string }) => {
        const errorMessage = i18n.getString(getThemeRecommendationErrorMessage(error.Code));
        throw new Error(errorMessage);
      })
      .finally(() => {
        fetchPerfMarker?.done();
      });
  }, [i18n, onFetchAssetsSucceeded, repository]);

  const apply = useCallback((themes: Theme[]) => {
    const selectedThemes = themes.filter((theme) => theme.IsSelected);
    if (selectedThemes.length === 0) {
      return Promise.reject(new Error(i18n.getString(_TL_('You must select at least one theme.'))));
    }

    // wait for the assets to finish fetching
    return fetchThemeAssetsPromise!.then((assetsRecommendations) => {
      const themesAndAssets = assetsRecommendations?.RecommendedAssets?.Themes;
      if (!themesAndAssets || themesAndAssets.length === 0) {
        return Promise.reject(new Error(i18n.getString(getThemeRecommendationErrorMessage(RecommendationError.NO_RECOMMENDATION))));
      }

      // get the assets for the selected themes only
      const selectedAssets = themesAndAssets.filter((assetsRecommendation: any) => selectedThemes.some((selectedTheme) => selectedTheme.Name === assetsRecommendation.Name));

      // log all returned themes, and assets for the selected themes only
      const selectedAssetsRecommendationData : AssetsRecommendationData = {
        RecommendedAssets: {
          Descriptions: _.reduce(selectedAssets, (memo : TextAssetRecommendation[], selectedAsset: ThemeWithAssets) => memo.concat(selectedAsset.Descriptions), []),
          ShortHeadlines: _.reduce(selectedAssets, (memo : TextAssetRecommendation[], selectedAsset: ThemeWithAssets) => memo.concat(selectedAsset.ShortHeadlines), []),
          LongHeadlines: _.reduce(selectedAssets, (memo : TextAssetRecommendation[], selectedAsset: ThemeWithAssets) => memo.concat(selectedAsset.LongHeadlines), []),
          ImageAssets: _.reduce(selectedAssets, (memo : ThemeImage[], selectedAsset: ThemeWithAssets) => memo.concat(selectedAsset.Images), []),
          Themes: themesAndAssets,
        },
      };
      onFetchAssetsSucceeded?.(finalUrl, selectedAssetsRecommendationData);

      return onApply(selectedThemes, selectedAssets);
    });
  }, [fetchThemeAssetsPromise, i18n, onApply]);

  const close = useCallback(() => {
    onClosed?.();
    repository!.cancelFetchThemesJob();
  }, [onClosed, repository]);

  const handleFetchSucceeded = useCallback((themes: Theme[]) => {
    onFetchSucceeded?.(finalUrl, themes);
  }, [onFetchSucceeded]);

  return (
    <ThemesRecommendationDialog
      className="copilot-themes-recommendation-dialog"
      i18n={i18n}
      open={open}
      finalUrl={finalUrl}
      fetchFn={fetch}
      applyFn={apply}
      onClose={close}
      onFetchSucceeded={handleFetchSucceeded}
      onFetchFailed={onFetchFailed}
      onSelectedThemesChanged={onSelectedThemesChanged}
      onApplySucceeded={onApplySucceeded}
      onApplyFailed={onApplyFailed}
      isNewLogo={isNewLogo}
      copyOverrides={copyOverrides}
    />
  );
};
